import { useState, memo, useCallback, useEffect, useRef } from 'react';
import { Box, TextInput, ActionIcon, Loader, Tooltip, Paper, Text, Stack } from '@mantine/core';
import { IconSend, IconX, IconMessage } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications'; // Ensure @mantine/notifications is configured at app root

import classes from './itinerary-input.module.css';

interface ItineraryInputProps {
  isStreaming: boolean;
  onSend: (msg: string) => void;
  autoFocus?: boolean;
  initialValue?: string;
  onAbort?: () => void;
  history?: string[];
}

const STREAM_STATUS_MESSAGES = [
  'Generating itinerary…',
  'Still working—curating experiences…',
  'Cross‑checking locations…',
  'Optimizing timings…',
  'Almost there…',
];

const TOAST_THRESHOLD_SECONDS = 20;

const ItineraryInput = memo(function ChangeRequestInput({
  isStreaming,
  onSend,
  autoFocus,
  initialValue = '',
  onAbort,
  history = [],
}: ItineraryInputProps) {
  const [message, setMessage] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);
  const canSend = !isStreaming && message.trim().length > 0;

  // Streaming feedback
  const [statusIndex, setStatusIndex] = useState(0);
  const [elapsedSec, setElapsedSec] = useState(0);
  const startTimeRef = useRef<number | null>(null);
  const toastShownRef = useRef(false);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      const savedY = window.scrollY;
      inputRef.current.focus({ preventScroll: true });
      // Restore in case browser ignored preventScroll
      window.scrollTo({ top: savedY, behavior: 'instant' });
    }
  }, [autoFocus]);

  useEffect(() => {
    if (isStreaming) {
      cancelledRef.current = false;
      startTimeRef.current = Date.now();
      setElapsedSec(0);
      setStatusIndex(0);
      toastShownRef.current = false;

      const rotateId = setInterval(() => {
        setStatusIndex((i) => (i + 1) % STREAM_STATUS_MESSAGES.length);
      }, 4000);

      const elapsedId = setInterval(() => {
        if (startTimeRef.current) {
          const secs = Math.floor((Date.now() - startTimeRef.current) / 1000);
          setElapsedSec(secs);
          if (!toastShownRef.current && secs >= TOAST_THRESHOLD_SECONDS) {
            toastShownRef.current = true;
            notifications.show({
              id: 'long-gen-info',
              title: 'Still generating',
              message: 'Large itineraries can take a bit. Thanks for waiting.',
              autoClose: 5000,
              withBorder: true,
            });
          }
        }
      }, 1000);

      return () => {
        clearInterval(rotateId);
        clearInterval(elapsedId);
      };
    } else {
      startTimeRef.current = null;
      setElapsedSec(0);
      setStatusIndex(0);
    }
  }, [isStreaming]);

  const handleSend = useCallback(() => {
    if (!canSend) return;
    const trimmed = message.trim();
    onSend(trimmed);
    setMessage('');
  }, [canSend, message, onSend]);

  const handleChange = (val: string) => {
    setMessage(val);
  };

  const handleAbort = () => {
    if (!isStreaming) return;
    cancelledRef.current = true;
    onAbort?.();
  };

  const dynamicPlaceholder = isStreaming
    ? `${STREAM_STATUS_MESSAGES[statusIndex]}${elapsedSec > 5 ? ` (${elapsedSec}s)` : ''}`
    : cancelledRef.current
      ? 'Generation cancelled. Refine and try again…'
      : 'Please let me know what changes you would like to make to the itinerary.';

  return (
    <Paper mt="sm" w="70%" withBorder radius="md" shadow="xs" p="sm" ml={15}>
      {history.length > 0 && (
        <Stack gap={4} mb="sm">
          {history.map((entry, i) => (
            <Box
              key={i}
              px="sm"
              py={6}
              style={{
                background: 'var(--mantine-color-gray-0)',
                borderRadius: 'var(--mantine-radius-sm)',
                borderLeft: '3px solid var(--mantine-color-green-6)',
              }}
            >
              <Text size="sm" c="dimmed">
                {entry}
              </Text>
            </Box>
          ))}
        </Stack>
      )}
      <TextInput
        ref={inputRef}
        classNames={{ input: classes.input }}
        value={message}
        label="VoyAssist: Your Itinerary Assistant"
        description={
          isStreaming
            ? 'Generating… you can cancel if needed.'
            : 'What would you like to change?  Press Enter or click send icon.'
        }
        placeholder={dynamicPlaceholder}
        size="md"
        radius="md"
        disabled={isStreaming}
        leftSection={<IconMessage size={16} />}
        leftSectionPointerEvents="none"
        onChange={(e) => handleChange(e.currentTarget.value)}
        rightSection={
          isStreaming ? (
            <Box style={{ display: 'flex', alignItems: 'center', gap: 6, paddingRight: 4 }}>
              <Tooltip label="Generating" withArrow>
                <Loader size="xs" />
              </Tooltip>
              {onAbort && (
                <Tooltip label="Cancel" withArrow>
                  <ActionIcon variant="subtle" color="red" onClick={handleAbort} aria-label="Cancel generation">
                    <IconX size={16} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Box>
          ) : (
            <ActionIcon
              variant="filled"
              color={canSend ? 'blue' : 'gray'}
              onClick={handleSend}
              disabled={!canSend}
              aria-label="Send request"
              title="Send"
            >
              <IconSend size={18} />
            </ActionIcon>
          )
        }
        rightSectionPointerEvents={isStreaming ? 'auto' : canSend ? 'auto' : 'none'}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && canSend) {
            e.preventDefault();
            handleSend();
          }
          if (e.key === 'Escape' && isStreaming) {
            e.preventDefault();
            handleAbort();
          }
        }}
      />
    </Paper>
  );
});

export default ItineraryInput;
